import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { auth as authApi, User, ApiError } from '../lib/api'

interface AuthContextType {
  user: User | null
  loading: boolean
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Restore session from cookie on mount
    authApi
      .me()
      .then(setUser)
      .catch(() => {
        // No valid session — leave user null so ProtectedRoute
        // redirects to /login
        setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const signUp = async (email: string, password: string) => {
    try {
      await authApi.signup(email, password)
      await authApi.login(email, password)
      const userData = await authApi.me()
      setUser(userData)
      return { error: null }
    } catch (error) {
      return {
        error:
          error instanceof ApiError || error instanceof Error
            ? error
            : new Error(String(error)),
      }
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      await authApi.login(email, password)
      const userData = await authApi.me()
      setUser(userData)
      return { error: null }
    } catch (error) {
      return {
        error:
          error instanceof ApiError || error instanceof Error
            ? error
            : new Error(String(error)),
      }
    }
  }

  const signOut = async () => {
    try {
      await authApi.logout()
    } catch (e) {
      console.error('Logout error:', e)
    }
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const userData = await authApi.me()
      setUser(userData)
    } catch {
      setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, signUp, signIn, signOut, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
